import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqItems = [
  { question: "What is a Recruiting Agency Association?", answer: "A Recruiting Agency Association is an organized body that brings together licensed recruitment and manpower agencies to promote ethical recruitment, improve industry standards, resolve common issues, and strengthen collaboration among members. It also acts as a unified voice representing the interests of recruitment agencies at national and international platforms." },
  { question: "What is AIRAA?", answer: "AIRAA (All India Recruiting Agencies Association) is a non-profit organization representing over 1,700 licensed recruiting agencies and 200+ skill development companies across India. AIRAA works to enhance ethical recruitment practices, support global workforce mobility, engage with government bodies, and provide a strong platform for knowledge sharing, industry collaboration, and policy advocacy." },
  { question: "How to Register with AIRAA?", answer: `You can register with AIRAA by completing the online membership form on the official website.\n\nSteps:\n• Click on the AIRAA registration link: airaa.co.in/registration\n• Complete the form with your agency's details\n• Upload required documents\n• Submit the application\n• After payment, you will receive your Membership Certificate\n\nNote: Membership is currently offered free for 2026. Validity until March 2027.` },
  { question: "How do I become an AIRAA member?", answer: "Joining AIRAA is simple. Any licensed recruiting agency, skill development company, or any company in India is eligible. Just submit your application through the registration link. After review and approval, your membership will be activated.\n\nNote: AIRAA is currently offering 1-year free membership." },
  { question: "What Are the Benefits of AIRAA Membership?", answer: "AIRAA provides several advantages:\n• Enhanced Credibility: Membership strengthens your agency's reputation\n• Networking: Connect with 1,700+ agencies and 200+ skill companies\n• International Partnerships: Collaborate with overseas employers and global stakeholders\n• Government Advocacy: AIRAA represents members with ministries and regulatory authorities\n• Training & Capacity Building: Access workshops, compliance training, and skilling initiatives\n• Ethical Recruitment Support: Promote safe, transparent recruitment practices" },
  { question: "What is PDOT centre support and how does AIRAA help?", answer: "AIRAA assists member agencies in setting up and managing PDOT (Protector of Emigrants) centres. We provide documentation guidance, liaison with the Ministry of External Affairs, and ensure compliance with emigration regulations." },
  { question: "Does AIRAA offer skill verification for jobs in KSA (Saudi Arabia)?", answer: "Yes, AIRAA facilitates skill verification and KSA-specific certification programmes. We partner with government-approved training centres to assess and certify candidates, ensuring they meet Gulf country employer standards." },
  { question: "How can I report a fraudulent recruitment agency?", answer: "You can report fraudulent agencies directly through the AIRAA helpline (+91 7738355865) or via our website's complaint portal. We also share official MEA advisories and maintain a public list of unregistered agencies on our Fraud Alerts page." },
  { question: "What community initiatives does AIRAA run?", answer: "AIRAA organizes free medical camps, blood donation drives, cancer patient support programmes, and vocational training for underprivileged youth. These initiatives are part of our social responsibility to empower workers and local communities." },
  { question: "What are the benefits of joining AIRAA as a recruiting agency?", answer: "Members get MEA policy advocacy, PDOT centre support, skill verification services, legal guidance, access to global employer networks, training discounts, and representation in chambers like ASSOCHAM and Indo-German Chamber of Commerce." },
];

const FaqItem = ({ item, index, isOpen, onToggle }) => (
  <div
    itemScope
    itemProp="mainEntity"
    itemType="https://schema.org/Question"
    className={`bg-white rounded-2xl border overflow-hidden transition-all duration-200 ${isOpen ? "border-emerald-200 shadow-md shadow-emerald-100/50" : "border-slate-100 shadow-sm hover:border-slate-200 hover:shadow"}`}
  >
    <button
      onClick={() => onToggle(index)}
      className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 focus:outline-none"
      aria-expanded={isOpen}
    >
      <h3 itemProp="name" className={`text-sm md:text-base font-semibold leading-snug transition-colors ${isOpen ? "text-blue-800" : "text-slate-800"}`}>
        {item.question}
      </h3>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? "bg-emerald-600 text-white rotate-180" : "bg-slate-100 text-slate-500"}`}>
        <ChevronDown size={15} />
      </div>
    </button>

    {/* ✅ Always rendered for Google's crawler — visibility controlled via max-height */}
    <div
      itemScope
      itemProp="acceptedAnswer"
      itemType="https://schema.org/Answer"
      style={{
        maxHeight: isOpen ? "1000px" : "0px",
        overflow: "hidden",
        transition: "max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        itemProp="text"
        className="px-6 pb-5 text-sm text-slate-600 leading-relaxed whitespace-pre-line border-t border-slate-50 pt-3"
      >
        {item.answer}
      </div>
    </div>
  </div>
);

const HomePageFaq = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="py-10 px-0">
      <div className="text-center mb-8">
        <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Support</span>
        <h2 className="text-3xl md:text-4xl font-extrabold text-blue-950 mb-3">
          Frequently Asked{" "}
          <span className="text-emerald-500">Questions</span>
        </h2>
        <p className="text-slate-500 text-sm max-w-lg mx-auto">
          Everything you need to know about AIRAA membership, registration, and our services.
        </p>
      </div>

      <div
        itemScope
        itemType="https://schema.org/FAQPage"
        className="space-y-3 max-w-3xl mx-auto"
      >
        {faqItems.map((item, i) => (
          <FaqItem key={i} item={item} index={i} isOpen={openIndex === i} onToggle={toggle} />
        ))}
      </div>
    </section>
  );
};

export default HomePageFaq;
